/**
 * DataTable
 *
 * Headless TanStack Table v8 wrapper that renders a React Bootstrap
 * <Table hover responsive>. Adds:
 * - Click-to-sort headers (asc -> desc -> none) with shift-click multi-sort
 * - Resizable columns persisted to localStorage
 * - Spreadsheet-like range selection + Ctrl+C TSV copy
 *
 * @module components/common/DataTable
 */

import React, { useMemo, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Table } from 'react-bootstrap';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import ColumnHeader from './ColumnHeader';
import useColumnSizing from './useColumnSizing';
import useRangeSelection from './useRangeSelection';
import './DataTable.css';

const stripHtml = (val) => {
  if (val == null) return '';
  if (typeof val === 'string' || typeof val === 'number') return String(val);
  // For React nodes, try to extract text
  if (React.isValidElement(val)) {
    const txt = extractTextFromNode(val);
    return txt;
  }
  return String(val);
};

const extractTextFromNode = (node) => {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractTextFromNode).join('');
  if (React.isValidElement(node)) {
    const children = node.props && node.props.children;
    return extractTextFromNode(children);
  }
  return '';
};

/**
 * Convert consumer-friendly column defs to TanStack column defs.
 */
const buildColumnDefs = (columns) =>
  columns.map((col) => {
    const def = {
      id: col.id,
      header: col.header,
      enableSorting: col.sortable !== false,
      enableResizing: col.resizable !== false,
      meta: {
        align: col.align || 'left',
        noSelect: !!col.noSelect,
      },
    };

    if (col.minWidth) def.minSize = col.minWidth;
    if (col.maxWidth) def.maxSize = col.maxWidth;

    if (typeof col.accessor === 'function') {
      def.accessorFn = col.accessor;
    } else if (typeof col.accessor === 'string') {
      def.accessorKey = col.accessor;
    } else {
      // No accessor - id-only column (e.g. actions)
      def.accessorFn = () => null;
    }

    if (col.cell) {
      def.cell = (ctx) => col.cell(ctx.row.original, ctx);
    }

    return def;
  });

const DataTable = ({
  tableId,
  data,
  columns,
  defaultSort,
  enableRangeSelection = true,
  enableColumnResize = true,
  rowKey,
  onRowClick,
  emptyState,
  loading = false,
  className = '',
}) => {
  const wrapperRef = useRef(null);

  const columnDefs = useMemo(() => buildColumnDefs(columns), [columns]);

  const [columnSizing, setColumnSizing] = useColumnSizing(tableId);
  const [sorting, setSorting] = React.useState(defaultSort ? [defaultSort] : []);

  const table = useReactTable({
    data: data || [],
    columns: columnDefs,
    state: {
      sorting,
      columnSizing,
    },
    onSortingChange: setSorting,
    onColumnSizingChange: setColumnSizing,
    enableMultiSort: true,
    enableColumnResizing: enableColumnResize,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: rowKey ? (row) => String(rowKey(row)) : undefined,
  });

  const sortedRows = table.getRowModel().rows;
  const visibleColumns = table.getVisibleLeafColumns();
  const rowCount = sortedRows.length;
  const colCount = visibleColumns.length;

  const getCellText = useCallback(
    (rowIdx, colIdx) => {
      const row = sortedRows[rowIdx];
      const col = visibleColumns[colIdx];
      if (!row || !col) return '';
      const cell = row.getAllCells().find((c) => c.column.id === col.id);
      if (!cell) return '';
      // Prefer raw value when no custom cell renderer
      const raw = cell.getValue();
      if (col.columnDef.cell && typeof col.columnDef.cell === 'function') {
        const rendered = flexRender(col.columnDef.cell, cell.getContext());
        return stripHtml(rendered);
      }
      return raw == null ? '' : String(raw);
    },
    [sortedRows, visibleColumns]
  );

  const range = useRangeSelection({
    enabled: enableRangeSelection,
    rowCount,
    colCount,
    getCellText,
    containerRef: wrapperRef,
  });

  const handleRowClick = (row, evt) => {
    if (!onRowClick) return;
    // Don't trigger row click for interactive elements or cells marked no-select
    const target = evt.target;
    if (target.closest('[data-no-select]')) return;
    let cur = target;
    let depth = 0;
    while (cur && depth < 6) {
      const tag = cur.tagName;
      if (tag === 'BUTTON' || tag === 'A' || tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
      cur = cur.parentElement;
      depth += 1;
    }
    onRowClick(row.original, evt);
  };

  const isEmpty = !loading && rowCount === 0;

  return (
    <div
      className={`dt-wrapper ${className}`.trim()}
      ref={wrapperRef}
      tabIndex={0}
      role="grid"
      aria-rowcount={rowCount}
      aria-colcount={colCount}
    >
      <Table hover responsive className="mb-0">
        <thead className="bg-light">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <ColumnHeader
                  key={header.id}
                  header={header}
                  enableResize={enableColumnResize}
                />
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {isEmpty ? (
            <tr>
              <td colSpan={colCount} className="dt-empty-state">
                {emptyState || <span>No hay datos</span>}
              </td>
            </tr>
          ) : (
            sortedRows.map((row, rowIdx) => {
              const cells = row.getVisibleCells();
              return (
                <tr
                  key={row.id}
                  onClick={onRowClick ? (e) => handleRowClick(row, e) : undefined}
                  style={onRowClick ? { cursor: 'pointer' } : undefined}
                  className="align-middle"
                >
                  {cells.map((cell, colIdx) => {
                    const align = cell.column.columnDef.meta?.align || 'left';
                    const noSelect = cell.column.columnDef.meta?.noSelect;
                    const selected = !noSelect && range.isCellSelected(rowIdx, colIdx);
                    const anchored = !noSelect && range.isCellAnchor(rowIdx, colIdx);
                    const className = [
                      `dt-cell-align-${align}`,
                      selected ? 'dt-cell-selected' : '',
                      anchored ? 'dt-cell-anchor' : '',
                      noSelect ? 'dt-no-select' : '',
                    ]
                      .filter(Boolean)
                      .join(' ');

                    return (
                      <td
                        key={cell.id}
                        className={className}
                        data-row={rowIdx}
                        data-col={colIdx}
                        data-no-select={noSelect ? '' : undefined}
                        onMouseDown={range.enabled && !noSelect ? range.handlers.onMouseDown : undefined}
                        onMouseEnter={range.enabled && !noSelect ? range.handlers.onMouseEnter : undefined}
                        onMouseUp={range.enabled && !noSelect ? range.handlers.onMouseUp : undefined}
                        style={{
                          width: cell.column.getSize() ? `${cell.column.getSize()}px` : undefined,
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </Table>
      {loading && (
        <div className="dt-loading-overlay">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      )}
    </div>
  );
};

DataTable.propTypes = {
  tableId: PropTypes.string.isRequired,
  data: PropTypes.array,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      header: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.func]).isRequired,
      accessor: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
      cell: PropTypes.func,
      sortable: PropTypes.bool,
      resizable: PropTypes.bool,
      minWidth: PropTypes.number,
      maxWidth: PropTypes.number,
      align: PropTypes.oneOf(['left', 'center', 'right']),
      noSelect: PropTypes.bool,
    })
  ).isRequired,
  defaultSort: PropTypes.shape({
    id: PropTypes.string.isRequired,
    desc: PropTypes.bool,
  }),
  enableRangeSelection: PropTypes.bool,
  enableColumnResize: PropTypes.bool,
  rowKey: PropTypes.func.isRequired,
  onRowClick: PropTypes.func,
  emptyState: PropTypes.node,
  loading: PropTypes.bool,
  className: PropTypes.string,
};

export default DataTable;
